import { useEffect } from 'react';
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router';
import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  hasChunkReloadAttempt,
  isChunkLoadError,
  markChunkReloadAttempt,
} from '@/lib/chunkRecovery';
import { isRunningStandalone } from '@/lib/pwa';

function getErrorMessage(error: unknown): string {
  if (isRouteErrorResponse(error)) {
    return error.data?.message ?? `${error.status} ${error.statusText}`;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Une erreur inattendue est survenue.';
}

export default function AppRouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const message = getErrorMessage(error);
  const chunkError = isChunkLoadError(error);
  const alreadyRetried = chunkError && hasChunkReloadAttempt(location.pathname);

  const isServerError = isRouteErrorResponse(error) && error.status >= 500;

  useEffect(() => {
    if (chunkError) {
      Sentry.captureException(error, {
        tags: {
          error_kind: 'chunk_load_error',
          frontend_version: __APP_VERSION__,
          requested_route: location.pathname,
          pwa: isRunningStandalone(),
          retry: alreadyRetried ? 'exhausted' : 'automatic',
        },
      });

      if (!alreadyRetried) {
        markChunkReloadAttempt(location.pathname);
        window.location.reload();
      }
      return;
    }

    if (isServerError || !isRouteErrorResponse(error)) {
      Sentry.captureException(error);
    }
  }, [alreadyRetried, chunkError, error, isServerError, location.pathname]);

  if (chunkError && !alreadyRetried) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center bg-neutral-50 p-6 text-sm font-medium text-charcoal-900"
      >
        Mise à jour de Velko POS…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-6">
      <div className="w-full max-w-lg rounded-xl border-[length:var(--border-brutal)] border-charcoal-950 bg-white p-6 brutal-shadow-md">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-lg border-[length:var(--border-brutal-thin)] border-charcoal-950 bg-warning-light p-2">
            <AlertTriangle className="h-5 w-5 text-warning-dark" />
          </div>
          <h1 className="font-heading text-xl font-bold text-charcoal-900">
            {chunkError ? 'Version obsolète' : "Erreur de l'application"}
          </h1>
        </div>

        <p className="mb-2 text-sm text-neutral-700">
          {chunkError
            ? "Cette page appartient à une autre version de l'application. Recharge-la pour continuer."
            : 'Une erreur technique a interrompu cette page.'}
        </p>
        <p className="mb-6 rounded-md bg-neutral-100 px-3 py-2 font-mono text-xs text-neutral-700">
          {message}
        </p>

        <div className="flex flex-wrap gap-3">
          <Button type="button" variant="cta" onClick={() => navigate('/dashboard')}>
            Retour au tableau de bord
          </Button>
          <Button type="button" variant="outline" onClick={() => window.location.reload()}>
            Recharger la page
          </Button>
        </div>
      </div>
    </div>
  );
}
